import dynamic from 'next/dynamic';
import { FC, useCallback, useEffect, useState } from 'react';

import { AuthFormHeader } from 'src/authentication/sections/AuthFormHeader/AuthFormHeader';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { Image } from 'src/general/components/Image/Image';
import { getMicrocopy } from 'src/stores/microcopyStore';
import { pluralise } from 'src/utils/pluralise';

import { AuthFormDisplayState } from '../AuthForm.types';
import styles from './Forms.module.scss';

type SignupSuccessProps = {
  displayState: AuthFormDisplayState;
  name: string;
  onSuccess: () => void;
};

type SignupSuccessData = {
  [key in AuthFormDisplayState]: {
    image: string;
    content: MICROCOPY;
  };
};

const CarSuccess = dynamic(() => import('src/general/animations/CarSuccess').then((mod) => mod.CarSuccess), {
  ssr: false,
});

export const SignupSuccess: FC<SignupSuccessProps> = ({ displayState, onSuccess, ...props }) => {
  const [timer, setTimer] = useState(5);
  const timeOutCallback = useCallback(() => setTimer((currTimer) => currTimer - 1), []);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    if (timer > 0) {
      timeoutId = setTimeout(timeOutCallback, 1000);
    } else {
      onSuccess();
    }
    return () => timeoutId && clearTimeout(timeoutId);
  }, [timer, timeOutCallback]);

  const signupSuccessData: SignupSuccessData = {
    checkout: {
      image: '',
      content: MICROCOPY.AUTHENTICATION_SIGNUP_SUCCESS_CHECKOUT,
    },
    default: {
      image: '/images/components/SignupSuccess/PolaroidKeys.svg',
      content: MICROCOPY.AUTHENTICATION_SIGNUP_SUCCESS_NAVBAR,
    },
    watchlist: {
      image: '/images/components/SignupSuccess/PolaroidKeys.svg',
      content: MICROCOPY.AUTHENTICATION_SIGNUP_SUCCESS_WATCHLIST,
    },
    carAlerts: {
      image: '/images/components/SignupSuccess/PolaroidKeys.svg',
      content: MICROCOPY.AUTHENTICATION_SIGNUP_SUCCESS_CAR_ALERTS,
    },
    personalisedQuote: {
      image: '/images/components/SignupSuccess/PolaroidKeys.svg',
      content: MICROCOPY.AUTHENTICATION_SIGNUP_SUCCESS_PERSONALISED_QUOTE,
    },
    carmatch: {
      image: '/images/components/SignupSuccess/PolaroidKeys.svg',
      content: MICROCOPY.AUTHENTICATION_SIGNUP_SUCCESS_NAVBAR,
    },
  };

  return (
    <section>
      <AuthFormHeader />
      <div className={styles.signupSuccess}>
        <div className={styles.imageContainer}>
          {displayState === 'checkout' ? (
            <CarSuccess />
          ) : (
            <Image src={signupSuccessData[displayState].image} width={160} height={160} alt="logo" />
          )}
        </div>
        <div className={styles.contentContainer}>
          {getMicrocopy(signupSuccessData[displayState].content, true, {
            name: 'name' in props ? props.name : '',
            second: pluralise('second', timer),
          })}
          <button onClick={onSuccess} className={styles.signupSuccessButton} type="button" data-testid="success">
            {getMicrocopy(MICROCOPY.AUTHENTICATION_SIGNUP_SUCCESS_BUTTON)}
          </button>
        </div>
      </div>
    </section>
  );
};
